import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { Configuration, Middleware } from '../api/runtime';
import { apiStatsService } from './apiStatsService';
import config from '../config/env';
import { safeStorage } from '../utils/safeStorage';

class APIClient {
  private axiosInstance: AxiosInstance;
  private static instance: APIClient;
  // In-flight refresh promise. Concurrent 401s share this so the
  // refresh token is only spent once per expiry window.
  private refreshPromise: Promise<void> | null = null;

  private constructor() {
    this.axiosInstance = axios.create({
      baseURL: config.api.baseUrl,
      // 30s default — was 10s, which is below the time a slow but
      // healthy backend takes when the list pages fire hundreds of
      // parallel detail fetches (e.g. /users with 240+ users each
      // doing GET /v2/users/{id}/groups; POST /v2/users from the
      // Create modal then queues behind that flood and the axios
      // timeout cancels the request client-side even though the
      // backend returned 200). 30s matches typical SLA budgets for
      // mutation endpoints behind a busy proxy.
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  public static getInstance(): APIClient {
    if (!APIClient.instance) {
      APIClient.instance = new APIClient();
    }
    return APIClient.instance;
  }

  private setupInterceptors() {
    // Request interceptor to add auth token and start timing
    this.axiosInstance.interceptors.request.use(
      (config) => {
        const token = safeStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add timestamp for duration tracking
        (config as any).metadata = { startTime: Date.now() };

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling and statistics tracking
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        // Track successful request
        this.trackRequest(response.config, response.status);
        return response;
      },
      async (error) => {
        const originalRequest = error.config;

        // Track failed request (unless it's a retry)
        if (!originalRequest._retry) {
          this.trackRequest(originalRequest, error.response?.status || 0);
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            await this.refreshToken();
            const token = safeStorage.getItem('access_token');
            if (token) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return this.axiosInstance(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed, redirect to login
            this.handleAuthFailure();
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private trackRequest(config: any, status: number): void {
    try {
      const endTime = Date.now();
      const startTime = (config as any).metadata?.startTime || endTime;
      const duration = endTime - startTime;

      const endpoint = config.url || 'unknown';
      const method = (config.method || 'GET').toUpperCase();

      apiStatsService.recordRequest(endpoint, method, status, duration);
    } catch (error) {
      // Silently fail to avoid breaking the main request flow
      if (config.dev.debug) {
        console.warn('[API CLIENT DEBUG] Failed to track request:', error);
      }
    }
  }

  private refreshToken(): Promise<void> {
    // Coalesce concurrent calls. A burst of 401s would otherwise spend
    // (and invalidate) the refresh token N times in parallel, leaving
    // every caller after the first with a 400 from Keycloak.
    if (this.refreshPromise) {
      return this.refreshPromise;
    }
    this.refreshPromise = this._doRefreshToken().finally(() => {
      this.refreshPromise = null;
    });
    return this.refreshPromise;
  }

  private async _doRefreshToken(): Promise<void> {
    const refreshTokenValue = safeStorage.getItem('refresh_token');
    const clientId = config.oauth.clientId;
    const tokenUrl = config.oauth.tokenUrl;

    if (!refreshTokenValue) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: clientId,
          refresh_token: refreshTokenValue,
        }),
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const tokens = await response.json();
      safeStorage.setItem('access_token', tokens.access_token);
      if (tokens.refresh_token) {
        safeStorage.setItem('refresh_token', tokens.refresh_token);
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      throw error;
    }
  }

  private handleAuthFailure(): void {
    safeStorage.removeItem('access_token');
    safeStorage.removeItem('refresh_token');
    // In a real app, you might want to dispatch an event or use a global state manager
    // to notify components about the auth failure
    window.location.href = '/';
  }

  public getAxiosInstance(): AxiosInstance {
    return this.axiosInstance;
  }

  // Add convenience methods for direct HTTP calls.
  // The optional `opts` accepts a subset of AxiosRequestConfig — currently
  // just `signal` for AbortController support, kept narrow on purpose so
  // callers don't reach into raw axios from outside this module.
  public async get(url: string, opts?: { signal?: AbortSignal }): Promise<any> {
    const response = await this.axiosInstance.get(url, opts);
    return response.data;
  }

  public async post(url: string, data?: any, opts?: { signal?: AbortSignal }): Promise<any> {
    const response = await this.axiosInstance.post(url, data, opts);
    return response.data;
  }

  public async put(url: string, data?: any, opts?: { signal?: AbortSignal }): Promise<any> {
    const response = await this.axiosInstance.put(url, data, opts);
    return response.data;
  }

  public async patch(url: string, data?: any, opts?: { signal?: AbortSignal }): Promise<any> {
    const response = await this.axiosInstance.patch(url, data, opts);
    return response.data;
  }

  public async delete(url: string, opts?: { signal?: AbortSignal }): Promise<any> {
    const response = await this.axiosInstance.delete(url, opts);
    return response.data;
  }

  public getConfiguration(): Configuration {
    const debugEnabled = config.dev.debug;

    // Create middleware to ensure Authorization header is set and track statistics
    const authMiddleware: Middleware = {
      pre: async (context) => {
        const token = safeStorage.getItem('access_token');
        if (debugEnabled) {
          console.log('[API CLIENT DEBUG] Middleware - AccessToken:', token ? 'TOKEN_PRESENT' : 'NO_TOKEN');
          console.log('[API CLIENT DEBUG] Middleware - Request URL:', context.url);
          console.log('[API CLIENT DEBUG] Middleware - Current headers:', context.init.headers);
        }

        // Add timestamp for tracking
        const startTime = Date.now();
        (context as any).startTime = startTime;

        if (token) {
          // Ensure Authorization header is set
          const existingHeaders = context.init.headers || {};
          const headers: Record<string, string> = {};

          // Convert existing headers to plain object
          if (existingHeaders instanceof Headers) {
            existingHeaders.forEach((value, key) => {
              headers[key] = value;
            });
          } else if (Array.isArray(existingHeaders)) {
            existingHeaders.forEach(([key, value]) => {
              headers[key] = value;
            });
          } else {
            Object.assign(headers, existingHeaders);
          }

          // Add Authorization header
          headers['Authorization'] = `Bearer ${token}`;

          if (debugEnabled) {
            console.log('[API CLIENT DEBUG] Middleware - Added Authorization header');
          }

          return {
            ...context,
            init: {
              ...context.init,
              headers
            }
          };
        }
        return context;
      },
      post: async (context) => {
        // Handle 401 errors by attempting token refresh
        if (context.response.status === 401) {
          if (debugEnabled) {
            console.log('[API CLIENT DEBUG] Middleware - Got 401, attempting token refresh');
          }

          try {
            // Attempt to refresh token
            const apiClientInstance = APIClient.getInstance();
            await (apiClientInstance as any).refreshToken();

            // Get new token and retry request
            const newToken = safeStorage.getItem('access_token');
            if (newToken) {
              const headers: Record<string, string> = {};
              const existingHeaders = context.init.headers || {};

              // Convert existing headers
              if (existingHeaders instanceof Headers) {
                existingHeaders.forEach((value, key) => {
                  headers[key] = value;
                });
              } else if (Array.isArray(existingHeaders)) {
                existingHeaders.forEach(([key, value]) => {
                  headers[key] = value;
                });
              } else {
                Object.assign(headers, existingHeaders);
              }

              // Update Authorization header
              headers['Authorization'] = `Bearer ${newToken}`;

              if (debugEnabled) {
                console.log('[API CLIENT DEBUG] Middleware - Retrying request with new token');
              }

              // Retry the request
              const retryResponse = await fetch(context.url, {
                ...context.init,
                headers
              });

              // Track the retry
              try {
                const endTime = Date.now();
                const startTime = (context as any).startTime || endTime;
                const duration = endTime - startTime;
                const endpoint = context.url || 'unknown';
                const method = context.init.method || 'GET';
                apiStatsService.recordRequest(endpoint, method, retryResponse.status, duration);
              } catch (trackError) {
                // Ignore tracking errors
              }

              return retryResponse;
            }
          } catch (refreshError) {
            if (debugEnabled) {
              console.error('[API CLIENT DEBUG] Middleware - Token refresh failed:', refreshError);
            }
            // Refresh failed, clear auth and redirect
            safeStorage.removeItem('access_token');
            safeStorage.removeItem('refresh_token');
            window.location.href = '/';
          }
        }

        // Track the request after completion
        try {
          const endTime = Date.now();
          const startTime = (context as any).startTime || endTime;
          const duration = endTime - startTime;

          const endpoint = context.url || 'unknown';
          const method = context.init.method || 'GET';
          const status = context.response.status;

          apiStatsService.recordRequest(endpoint, method, status, duration);

          if (debugEnabled) {
            console.log('[API CLIENT DEBUG] Middleware - Tracked request:', {
              endpoint,
              method,
              status,
              duration: `${duration}ms`
            });
          }
        } catch (error) {
          if (debugEnabled) {
            console.warn('[API CLIENT DEBUG] Middleware - Failed to track request:', error);
          }
        }

        return context.response;
      }
    };

    return new Configuration({
      basePath: config.api.baseUrl,
      middleware: [authMiddleware],
      accessToken: () => {
        const token = safeStorage.getItem('access_token');
        if (debugEnabled) {
          console.log('[API CLIENT DEBUG] AccessToken requested:', token ? 'TOKEN_PRESENT' : 'NO_TOKEN');
          if (token) {
            console.log('[API CLIENT DEBUG] Token preview:', token.substring(0, 20) + '...');
          }
        }
        return token || '';
      },
    });
  }
}

export default APIClient;
export const apiClient = APIClient.getInstance();